import type { Licence } from './licence';

export interface LicenceKeeperOptions<U extends string> {
  /** The codes kept, unchecked, as the app stored them. */
  load: () => string[];
  /** Stores the codes to keep, replacing what was stored. */
  save: (codes: string[]) => void;
  /** The licence a code holds, null if it holds none (the app's `verifyLicence`, bound to its keys). */
  verify: (code: string) => Promise<Licence<U> | null>;
  /** Whether a signature can be checked here at all; by default, whether crypto.subtle exists. */
  canCheck?: () => boolean;
}

export interface LicenceKeeper<U extends string> {
  /** What the licences redeemed here unlock, all of them together; undefined while the ones kept
   * are checked. */
  getUnlocks: () => U[] | undefined;
  /** What they unlock once the ones kept have been checked: for a decision that can't wait for the
   * next change and mustn't be made on "undefined", like whether a sign-in may create an account. */
  whenChecked: () => Promise<U[]>;
  /** Redeems a code: kept and true if it holds a licence, false and everything as it was if not. */
  redeem: (code: string) => Promise<boolean>;
  /** Calls `listener` whenever the unlocks change; returns how to stop. */
  subscribe: (listener: () => void) => () => void;
}

/** A code kept, with the licence it was checked to hold. */
interface Kept<U extends string> {
  code: string;
  licence: Licence<U>;
}

const unlocksOf = <U extends string>(kept: Kept<U>[]): U[] => [...new Set(kept.flatMap((k) => k.licence.unlocks))];

const cryptoAvailable = () => typeof crypto !== 'undefined' && crypto.subtle !== undefined;

/**
 * The licences redeemed on a device, kept through `load` and `save` so each app chooses where. It
 * knows nothing of React or of any store; one that wants to follow it uses `subscribe`.
 */
export function createLicenceKeeper<U extends string>({
  load,
  save,
  verify,
  canCheck = cryptoAvailable,
}: LicenceKeeperOptions<U>): LicenceKeeper<U> {
  // Keys only add: a customer must never lose what they paid for by entering a new one, so every
  // code redeemed is kept and the device unlocks all that any of them does.
  let kept: Kept<U>[] = [];
  const listeners = new Set<() => void>();

  const codes = load();
  let unlocks: U[] | undefined = codes.length > 0 ? undefined : [];
  const update = () => {
    unlocks = unlocksOf(kept);
    listeners.forEach((listener) => listener());
  };

  // Checked again on every start, rather than trusted for having been checked once: what's kept
  // (localStorage, say) can be edited by hand. A code that doesn't hold up (changed, or signed by a
  // key since taken out) is dropped, so it isn't checked again for nothing — but only where a
  // signature can be checked at all. Over plain http (a phone trying the dev server by its address)
  // there's no crypto.subtle, every code fails, and dropping them would erase what was paid for.
  const checkable = canCheck();
  const checked = Promise.all(codes.map(async (code) => ({ code, licence: await verify(code) }))).then((results) => {
    kept = results.filter((r): r is Kept<U> => r.licence !== null);
    if (checkable && kept.length < codes.length) save(kept.map((k) => k.code));
    update();
  });

  return {
    getUnlocks: () => unlocks,
    whenChecked: async () => {
      await checked;
      return unlocksOf(kept);
    },
    redeem: async (code) => {
      const licence = await verify(code);
      if (!licence) return false;
      // A code redeemed while the ones kept are still being checked would be lost to that check.
      await checked;
      const stripped = code.replace(/\s/g, '');
      // Already kept, as that very code or as one for the same licence that unlocks all this does:
      // nothing to add.
      const covered = kept.some(
        (k) =>
          k.code === stripped || (k.licence.id === licence.id && licence.unlocks.every((u) => k.licence.unlocks.includes(u))),
      );
      if (!covered) {
        kept = [...kept, { code: stripped, licence }];
        save(kept.map((k) => k.code));
        update();
      }
      return true;
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
